import React, { useState } from 'react';
import { Table, Button, ButtonGroup } from 'react-bootstrap';
import ReservationDialog from './ReservationDialog';
import { Reservation } from '../../models/reserve';

interface ReservationTableProps {
  reservations: Reservation[];
  onUpdateReservation: (reservation: Reservation) => void;
  onDeleteReservation: (reservationId: number) => void;
}

const ReservationTable: React.FC<ReservationTableProps> = ({
  reservations,
  onUpdateReservation,
  onDeleteReservation,
}) => {
  const [selectedReservation, setSelectedReservation] =
    useState<Reservation | null>(null);
  const [dialogType, setDialogType] = useState<'view' | 'edit' | 'delete'>(
    'view'
  );
  const [showDialog, setShowDialog] = useState<boolean>(false);

  // 詳細確認ボタンの処理
  const handleViewClick = (reservation: Reservation): void => {
    setSelectedReservation(reservation);
    setDialogType('view');
    setShowDialog(true);
  };

  // 編集ボタンの処理
  const handleEditClick = (reservation: Reservation): void => {
    setSelectedReservation(reservation);
    setDialogType('edit');
    setShowDialog(true);
  };

  // 削除ボタンの処理
  const handleDeleteClick = (reservation: Reservation): void => {
    setSelectedReservation(reservation);
    setDialogType('delete');
    setShowDialog(true);
  };

  // ダイアログの結果を受け取る
  const handleDialogConfirm = (updatedReservation?: Reservation): void => {
    if (dialogType === 'edit' && updatedReservation) {
      onUpdateReservation(updatedReservation);
    } else if (dialogType === 'delete' && selectedReservation) {
      onDeleteReservation(selectedReservation.id);
    }
    setShowDialog(false);
    setSelectedReservation(null);
  };

  const handleDialogClose = (): void => {
    setShowDialog(false);
    setSelectedReservation(null);
  };

  // 日付をフォーマットする関数
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // 支払い方法の日本語表示
  const getPaymentMethodText = (paymentMethod?: string): string => {
    switch (paymentMethod) {
      case 'bank':
        return '銀行振込';
      case 'cash':
        return '現金';
      case 'credit':
        return 'クレジット';
      default:
        return '未設定';
    }
  };

  // ステータスバッジの色を決定
  //   const getStatusVariant = (status: string): string => {
  //     switch (status) {
  //       case 'pending':
  //         return 'warning';
  //       case 'confirmed':
  //         return 'info';
  //       case 'completed':
  //         return 'success';
  //       case 'cancelled':
  //         return 'danger';
  //       default:
  //         return 'secondary';
  //     }
  //   };

  // ステータスの日本語表示
  //   const getStatusText = (status: string): string => {
  //     switch (status) {
  //       case 'pending':
  //         return '保留中';
  //       case 'confirmed':
  //         return '確定';
  //       case 'completed':
  //         return '完了';
  //       case 'cancelled':
  //         return 'キャンセル';
  //       default:
  //         return '不明';
  //     }
  //   };

  return (
    <>
      <div className="table-responsive">
        <Table className="reservation-table" striped hover>
          <thead className="table-dark">
            <tr>
              <th>ID</th>
              <th>予約者名</th>
              <th>予約日</th>
              <th>予約時間</th>
              <th>メニュー</th>
              <th>金額</th>
              <th>支払い方法</th>
              <th>ステータス</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {reservations.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center text-muted py-4">
                  予約データがありません
                </td>
              </tr>
            ) : (
              reservations.map(reservation => (
                <tr key={reservation.id}>
                  <td>{reservation.id}</td>
                  <td>{reservation.name}</td>
                  <td>{formatDate(reservation.date)}</td>
                  <td>{reservation.time}</td>
                  <td>{reservation.menu}</td>
                  <td>¥{reservation.totalPrice.toLocaleString()}</td>
                  <td>
                    <span
                      className={`badge ${
                        reservation.paymentMethod === 'bank'
                          ? 'bg-info'
                          : reservation.paymentMethod === 'cash'
                            ? 'bg-success'
                            : reservation.paymentMethod === 'credit'
                              ? 'bg-primary'
                              : 'bg-secondary'
                      }`}
                    >
                      {getPaymentMethodText(reservation.paymentMethod)}
                    </span>
                  </td>
                  {/* <td>
                    <Badge bg={getStatusVariant(reservation.status)}>
                      {getStatusText(reservation.status)}
                    </Badge>
                  </td> */}
                  <td></td>
                  <td>
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline-info"
                        onClick={() => handleViewClick(reservation)}
                        title="詳細確認"
                      >
                        <i className="bi bi-eye"></i>
                      </Button>
                      <Button
                        variant="outline-warning"
                        onClick={() => handleEditClick(reservation)}
                        title="編集"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() => handleDeleteClick(reservation)}
                        title="削除"
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* ダイアログ */}
      <ReservationDialog
        show={showDialog}
        reservation={selectedReservation}
        type={dialogType}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};

export default ReservationTable;
